import type { FeedbackItem, FinalishAnchor } from './types';
import { isSensitiveElement } from './dom';
import {
  canonicalDocumentTitle,
  canonicalPageKey,
  routeKeyMatchesPage,
} from './route';

export function describeElement(
  element: Element,
  event: PointerEvent,
): FinalishAnchor {
  const rect = element.getBoundingClientRect();
  const masked = isSensitiveElement(element);
  return {
    version: 1,
    routeKey: canonicalPageKey(),
    documentTitle: canonicalDocumentTitle(),
    stableId:
      element.getAttribute('data-finalish-id') ??
      element.getAttribute('data-testid') ??
      element.id ??
      null,
    cssSelector: buildCssSelector(element),
    xpathFallback: buildXPath(element),
    tag: element.tagName.toLowerCase(),
    textFingerprint: masked ? null : normalizeText(element.textContent),
    normalizedX: rect.width ? (event.clientX - rect.left) / rect.width : 0.5,
    normalizedY: rect.height ? (event.clientY - rect.top) / rect.height : 0.5,
    elementRect: {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    },
    masked,
  };
}

export function resolveFeedbackPoint(item: FeedbackItem): {
  x: number;
  y: number;
  found: boolean;
} {
  const anchor = readAnchor(item.selector);
  const element = anchor ? resolveElement(anchor) : null;
  if (element && anchor && isElementRenderable(element)) {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.left + rect.width * clamp(anchor.normalizedX, 0, 1),
      y: rect.top + rect.height * clamp(anchor.normalizedY, 0, 1),
      found: true,
    };
  }
  return {
    x: numberValue(item.position.x) - window.scrollX,
    y: numberValue(item.position.y) - window.scrollY,
    found: false,
  };
}

export function feedbackBelongsToCurrentRoute(item: FeedbackItem): boolean {
  return feedbackBelongsToPage(item, canonicalPageKey());
}

export function feedbackBelongsToPage(
  item: FeedbackItem,
  pageKey: string,
  _documentTitle: string = canonicalDocumentTitle(),
): boolean {
  const anchor = readAnchor(item.selector);
  if (anchor?.routeKey && !routeKeyMatchesPage(anchor.routeKey, pageKey)) {
    return false;
  }
  // documentTitle is descriptive anchor metadata, not a stable page identity.
  // SPAs routinely update it after a route has rendered or user data has loaded.
  return true;
}

export function isElementRenderable(element: Element): boolean {
  if (!element.isConnected) return false;

  let current: Element | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    const opacity = style.opacity.trim();
    if (
      current.hasAttribute('hidden') ||
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.getPropertyValue('content-visibility') === 'hidden' ||
      (opacity !== '' && Number(opacity) <= 0)
    ) {
      return false;
    }
    current = current.parentElement;
  }

  const rect = element.getBoundingClientRect();
  return (
    Number.isFinite(rect.left) &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.width) &&
    Number.isFinite(rect.height) &&
    rect.width > 0 &&
    rect.height > 0
  );
}

export function resolveElement(anchor: FinalishAnchor): Element | null {
  if (anchor.stableId) {
    const safe = CSS.escape(anchor.stableId);
    const stable = document.querySelector(
      `[data-finalish-id="${safe}"],[data-testid="${safe}"],#${safe}`,
    );
    if (stable) return stable;
  }
  try {
    const css = document.querySelector(anchor.cssSelector);
    if (css) return css;
  } catch {
    // Fall through to text and XPath recovery.
  }
  if (anchor.textFingerprint) {
    const candidates = document.querySelectorAll(anchor.tag);
    for (const candidate of candidates) {
      if (normalizeText(candidate.textContent) === anchor.textFingerprint) {
        return candidate;
      }
    }
  }
  try {
    return document.evaluate(
      anchor.xpathFallback,
      document,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null,
    ).singleNodeValue as Element | null;
  } catch {
    return null;
  }
}

export function readAnchor(
  value: Record<string, unknown> | null,
): FinalishAnchor | null {
  if (!value || value.version !== 1 || typeof value.cssSelector !== 'string') {
    return null;
  }
  return value as unknown as FinalishAnchor;
}

export function viewportSnapshot() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    devicePixelRatio: window.devicePixelRatio || 1,
  };
}

function buildCssSelector(element: Element): string {
  if (element.id) return `#${CSS.escape(element.id)}`;
  const parts: string[] = [];
  let current: Element | null = element;
  while (current && current !== document.documentElement && parts.length < 6) {
    let part = current.tagName.toLowerCase();
    const stableClass = [...current.classList].find(
      (name) => !/^(active|selected|hover|focus|css-|sc-)/i.test(name),
    );
    if (stableClass) part += `.${CSS.escape(stableClass)}`;
    const parent: Element | null = current.parentElement;
    if (parent) {
      const sameTags = [...parent.children].filter(
        (child) => child.tagName === current?.tagName,
      );
      if (sameTags.length > 1) {
        part += `:nth-of-type(${sameTags.indexOf(current) + 1})`;
      }
    }
    parts.unshift(part);
    current = parent;
  }
  return parts.join(' > ');
}

function buildXPath(element: Element): string {
  const parts: string[] = [];
  let current: Element | null = element;
  while (current && current.nodeType === Node.ELEMENT_NODE) {
    const parent: Element | null = current.parentElement;
    if (!parent) {
      parts.unshift(current.tagName.toLowerCase());
      break;
    }
    const siblings = [...parent.children].filter(
      (child) => child.tagName === current?.tagName,
    );
    parts.unshift(
      `${current.tagName.toLowerCase()}[${siblings.indexOf(current) + 1}]`,
    );
    current = parent;
  }
  return `/${parts.join('/')}`;
}

function normalizeText(value: string | null): string | null {
  const normalized = value?.replace(/\s+/g, ' ').trim().slice(0, 160);
  return normalized || null;
}

function numberValue(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}
